import * as React from 'react';
import styled from '@emotion/styled';
import { MuiButton } from '@onehope/design-system-ohw';
import theme from '../../../styles/themes/hopeTheme';

function getMaxWidth(textWrapperImage: string, isTextBoxOverlay: boolean) {
  if (isTextBoxOverlay) {
    return '55%';
  }
  if (textWrapperImage) {
    return '600px';
  }
  return '500px';
}

interface ImageTileProps {
  alt?: string;
  mobile: {
    src: string;
    height: number;
    width: number;
  };
  tablet: {
    src: string;
    height: number;
    width: number;
  };
  desktop: {
    src: string;
    height: number;
    width: number;
  };
}

interface DynamicTileProps {
  handleRouteChange: (input?: string) => void;
  config: {
    buttonText?: string;
    buttonLink?: string;
    buttonTwoText?: string | null;
    buttonTwoLink?: string | null;
    images: ImageTileProps;
    header: string;
    miniHeader?: string;
    summary: string;
    subSummary?: string;
    imageSide: string;
    isTextBoxOverlay?: true;
    textAlign: string;
    promoText?: string;
    headerTextAlign?: string;
    overlayLogo?: {
      alt: string;
      src: string;
      height: number;
      width: number;
    };
    textWrapperImage?: {
      alt: string;
      src: string;
      height: number;
      width: number;
    };
    miniTextOverlay?: string;
    textOverlay?: string;
    buttonOverlayText?: string;
    buttonOverlayLink?: string;
  };
}

const ButtonStyles = {
  maxWidth: 288,
};

const ImageTile = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
`;

const ImageWrapper = styled.div<ImageTileProps>`
  position: relative;
  height: 0;
  padding-top: ${({ mobile }) => `${(mobile.height / mobile.width) * 100}%`};
  @media screen and (min-width: 768px) {
    padding-top: ${({ tablet }) => `${(tablet.height / tablet.width) * 100}%`};
  }
  @media screen and (min-width: 1440px) {
    padding-top: ${({ desktop }) =>
      `${(desktop.height / desktop.width) * 100}%`};
  }
`;

const Container = styled.div<any>`
  display: flex;
  position: relative;
  flex-direction: column;
  @media screen and (min-width: 768px) {
    flex-wrap: wrap;
    flex-direction: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? 'row-reverse' : 'column'};
  }
  @media screen and (min-width: 1440px) {
    flex-wrap: wrap;
    flex-direction: ${({ imageSide }) =>
      imageSide === 'left' ? 'row' : 'row-reverse'};
  }
`;

const ImageContainer = styled.div<any>`
  position: relative;
  @media screen and (min-width: 768px) {
    width: ${({ isTextBoxOverlay }) => (isTextBoxOverlay ? '50%' : '100%')};
  }
  @media screen and (min-width: 1440px) {
    width: ${({ isTextBoxOverlay }) => (isTextBoxOverlay ? '55%' : '50%')};
  }
`;

const TextBoxWrapper = styled.div<any>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: ${theme.textColor};
  font-family: ${theme.headerBold};
  /* font-family: 'Playfair Display', 'proxima-nova', serif; */
  text-align: center;
  margin: 0 auto;
  padding: 40px 0;
  max-width: ${({ textWrapperImage }) =>
    textWrapperImage ? '600px' : '500px'};
  @media screen and (min-width: 768px) {
    text-align: ${({ textAlign }) => textAlign};
    width: ${({ isTextBoxOverlay }) => (isTextBoxOverlay ? '50%' : '100%')};
  }
  @media screen and (min-width: 1440px) {
    padding: 0;
    right: 47%;
    top: 50%;
    background-color: white;
    transform: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? ' translateY(-50%)' : 'none'};
    width: ${({ isTextBoxOverlay }) => (isTextBoxOverlay ? '740px' : '50%')};
    max-width: ${({ isTextBoxOverlay, textWrapperImage }) =>
      getMaxWidth(textWrapperImage, isTextBoxOverlay)};
    height: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? '640px' : 'inherit'};
    position: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? 'absolute' : 'unset'};
    box-shadow: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? '0 6px 20px 0 rgba(0, 0, 0, 0.15)' : 'none'};
  }
`;

const TextBoxContainer = styled.div<any>`
  padding: 16px;
  text-align: center;
  @media screen and (min-width: 768px) {
    text-align: ${({ textWrapperImage, textAlign, isTextBoxOverlay }) =>
      isTextBoxOverlay || textWrapperImage ? 'left' : textAlign};
    padding: ${({ textAlign }) =>
      textAlign === 'left' ? '16px 48px' : '16px'};
  }
  @media screen and (min-width: 1440px) {
    text-align: ${({ headerTextAlign }) => headerTextAlign || 'left'};
    width: 500px;
  }
`;

const Header = styled.div`
  font-size: 32px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.31;
  letter-spacing: -0.5px;
  font-family: 'Playfair Display', 'proxima-nova', serif;
  @media screen and (min-width: 1440px) {
    font-size: 44px;
    line-height: 1.32;
  }
`;

const MiniHeader = styled.div`
  font-size: 10px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.6;
  letter-spacing: 2px;
  padding-bottom: 24px;
  text-transform: uppercase;
`;

const Summary = styled.div<any>`
  padding: 24px 0;
  font-size: 16px;
  font-weight: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  @media screen and (min-width: 768px) {
    max-width: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? '288px' : 'inherit'};
  }
  @media screen and (min-width: 1440px) {
    max-width: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? '432px' : 'inherit'};
  }
`;

const SubSummary = styled.div<any>`
  font-size: 14px;
  font-weight: 600;
  font-style: italic;
  font-stretch: normal;
  line-height: 19px;
  letter-spacing: normal;
  padding-top: 16px;
`;

const Promo = styled.div`
  font-size: 14px;
  font-style: italic;
  font-weight: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #1a1a1a;
  /* padding-bottom: 24px; */
  padding-top: 26px;
`;

const TextImage = styled.img<any>`
  display: none;
  @media screen and (min-width: 768px) {
    height: ${({ height }) => `${height}px`};
    min-width: ${({ width }) => `${width}px`};
    display: inline-block;
  }
`;

const NewLogoDesktop = styled.img<any>`
  display: none;
  @media screen and (min-width: 1440px) {
    display: block;
    left: 0;
    top: 0;
    position: absolute;
    z-index: 1;
    height: 80px;
    width: 80px;
  }
`;

const NewLogoTablet = styled.img<any>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  height: 80px;
  width: 80px;
  @media screen and (min-width: 1440px) {
    display: none;
  }
`;

const MiniTextOverlay = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  position: absolute;
  top: 64px;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.6;
  letter-spacing: 2px;
  text-align: center;
  color: white;
  width: 100%;
  text-transform: uppercase;
  @media screen and (min-width: 768px) {
    top: 77px;
  }
  @media screen and (min-width: 1440px) {
    top: 128px;
  }
`;

const TextOverlay = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  position: absolute;
  top: 88px;
  font-size: 32px;
  font-weight: bold;
  line-height: 1.31;
  letter-spacing: -0.5px;
  text-align: center;
  color: white;
  @media screen and (min-width: 768px) {
    top: 101px;
  }
  @media screen and (min-width: 1440px) {
    top: 152px;
  }
`;

const ButtonOverlay = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  position: absolute;
  bottom: 64px;
  @media screen and (min-width: 768px) {
    bottom: 77px;
  }
  @media screen and (min-width: 1440px) {
    bottom: 128px;
  }
`;

function ResponsiveImage({ desktop, mobile, tablet, alt }: ImageTileProps) {
  return (
    <ImageWrapper mobile={mobile} tablet={tablet} desktop={desktop}>
      <picture>
        <source srcSet={desktop.src} media="(min-width: 1440px)" />
        <source srcSet={tablet.src} media="(min-width: 768px)" />
        <ImageTile srcSet={mobile.src} alt={alt} />
      </picture>
    </ImageWrapper>
  );
}

function createMarkup(summary: string) {
  return { __html: summary };
}

function DynamicTile({ config, handleRouteChange }: DynamicTileProps) {
  const onButtonClickOne = () => {
    const { buttonLink } = config;
    handleRouteChange(buttonLink);
  };

  const onButtonClickTwo = () => {
    const { buttonTwoLink } = config;
    if (buttonTwoLink) {
      handleRouteChange(buttonTwoLink);
    }
  };

  const onButtonOverlayClick = () => {
    const { buttonOverlayLink } = config;
    handleRouteChange(buttonOverlayLink);
  };

  const {
    images: { mobile, tablet, desktop, alt },
    summary,
    buttonText,
    header,
    miniHeader,
    imageSide,
    subSummary,
    promoText,
    textAlign,
    buttonTwoText,
    isTextBoxOverlay,
    textWrapperImage,
    overlayLogo,
    miniTextOverlay,
    textOverlay,
    headerTextAlign,
    buttonOverlayText,
  } = config;

  return (
    <Container imageSide={imageSide} isTextBoxOverlay={isTextBoxOverlay}>
      {overlayLogo && (
        <NewLogoTablet src={overlayLogo.src} alt={overlayLogo.alt} />
      )}
      <ImageContainer isTextBoxOverlay={isTextBoxOverlay}>
        <ResponsiveImage
          alt={alt}
          mobile={mobile}
          tablet={tablet}
          desktop={desktop}
        />
        {miniTextOverlay && (
          <MiniTextOverlay>{miniTextOverlay}</MiniTextOverlay>
        )}
        {textOverlay && <TextOverlay>{textOverlay}</TextOverlay>}
        {buttonOverlayText && (
          <ButtonOverlay>
            <MuiButton
              style={ButtonStyles}
              onClick={onButtonOverlayClick}
              type="SECONDARY"
              fullWidth
            >
              {buttonOverlayText}
            </MuiButton>
          </ButtonOverlay>
        )}
      </ImageContainer>
      <TextBoxWrapper
        textAlign={textAlign}
        textWrapperImage={textWrapperImage}
        isTextBoxOverlay={isTextBoxOverlay}
      >
        {overlayLogo && (
          <NewLogoDesktop src={overlayLogo.src} alt={overlayLogo.alt} />
        )}
        {textWrapperImage && (
          <TextImage
            alt={textWrapperImage.alt}
            width={textWrapperImage.width}
            height={textWrapperImage.height}
            src={textWrapperImage.src}
          />
        )}
        <TextBoxContainer
          textAlign={textAlign}
          headerTextAlign={headerTextAlign}
          textWrapperImage={textWrapperImage}
          isTextBoxOverlay={isTextBoxOverlay}
        >
          {miniHeader && <MiniHeader>{miniHeader}</MiniHeader>}
          <Header dangerouslySetInnerHTML={createMarkup(header)} />
          {subSummary && (
            <SubSummary dangerouslySetInnerHTML={createMarkup(subSummary)} />
          )}
          <Summary
            isTextBoxOverlay={isTextBoxOverlay}
            dangerouslySetInnerHTML={createMarkup(summary)}
          />
          {buttonText && (
            <MuiButton
              style={ButtonStyles}
              onClick={onButtonClickOne}
              type="PRIMARY"
              fullWidth
            >
              {buttonText}
            </MuiButton>
          )}
          {promoText && <Promo>{promoText}</Promo>}
          {buttonTwoText && (
            <MuiButton
              style={ButtonStyles}
              onClick={onButtonClickTwo}
              type="SECONDARY"
              fullWidth
            >
              {buttonTwoText}
            </MuiButton>
          )}
        </TextBoxContainer>
      </TextBoxWrapper>
    </Container>
  );
}

export default DynamicTile;
